/**
 * IntroScreen Component
 * 
 * Guided intro tour with welcome screen and spotlight highlighting.
 * Shows users how to use the app with an animated preview and step-by-step guide.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import styles from './IntroScreen.module.css';

/**
 * IntroScreen component props
 */
export interface IntroScreenProps {
  /** Callback when intro is completed or skipped */
  onComplete: () => void;
}

/**
 * Intro step configuration
 */
interface IntroStep {
  /** Step number (1-5) */
  step: number;
  /** Data attribute selector for the element to highlight */
  selector: string;
  /** Translation key for the step description */
  descriptionKey: string;
}

const INTRO_STEPS: IntroStep[] = [
  { step: 1, selector: '[data-intro-step="1"]', descriptionKey: 'step1' },
  { step: 2, selector: '[data-intro-step="2"]', descriptionKey: 'step2' },
  { step: 3, selector: '[data-intro-step="3"]', descriptionKey: 'step3' },
  { step: 4, selector: '[data-intro-step="4"]', descriptionKey: 'step4' },
  { step: 5, selector: '[data-intro-step="5"]', descriptionKey: 'step5' },
];

/**
 * IntroScreen Component
 * 
 * Shows a welcome screen with animated preview, followed by a spotlight tour.
 */
export const IntroScreen: React.FC<IntroScreenProps> = ({ onComplete }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<number>(0); // 0 = welcome, 1-5 = tour steps
  const [spotlightPosition, setSpotlightPosition] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
    placement: 'top' | 'bottom' | 'left' | 'right';
  } | null>(null);

  /**
   * Calculate spotlight and tooltip positions for current step
   */
  const calculatePositions = useCallback(() => {
    if (currentStep === 0) {
      setSpotlightPosition(null);
      setTooltipPosition(null);
      return;
    }

    const stepConfig = INTRO_STEPS[currentStep - 1];
    const element = document.querySelector(stepConfig.selector);

    if (!element) {
      console.warn(`Element not found for step ${currentStep}: ${stepConfig.selector}`);
      return;
    }

    const rect = element.getBoundingClientRect();
    const padding = 16;

    // Calculate spotlight position
    setSpotlightPosition({
      x: rect.left - padding,
      y: rect.top - padding,
      width: rect.width + padding * 2,
      height: rect.height + padding * 2,
    });

    // Calculate tooltip position
    const tooltipWidth = 320;
    const tooltipHeight = 150;
    const margin = 24;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = 0;
    let left = 0;
    let placement: 'top' | 'bottom' | 'left' | 'right' = 'bottom';

    // Special handling for share button (step 5) - position above to avoid blocking
    if (currentStep === 5) {
      placement = 'top';
      // Position tooltip above the spotlight (which includes padding)
      // spotlight top = rect.top - padding
      // Add extra spacing (60px) to ensure tooltip is well above the button
      const spotlightTop = rect.top - padding;
      const extraSpacing = 60;
      const tooltipTop = spotlightTop - margin - tooltipHeight - extraSpacing;
      
      if (tooltipTop >= 16) {
        // Enough space above the spotlight - position tooltip there
        top = tooltipTop;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
      } else {
        // Not enough space - position at top of screen, centered
        top = 16;
        left = viewportWidth / 2 - tooltipWidth / 2;
      }
    }
    // Default positioning for other steps
    else {
      // Try to place tooltip below the element
      if (rect.bottom + margin + tooltipHeight < viewportHeight) {
        placement = 'bottom';
        top = rect.bottom + margin;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
      }
      // Try above
      else if (rect.top - margin - tooltipHeight > 0) {
        placement = 'top';
        top = rect.top - margin - tooltipHeight;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
      }
      // Try right
      else if (rect.right + margin + tooltipWidth < viewportWidth) {
        placement = 'right';
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + margin;
      }
      // Try left
      else if (rect.left - margin - tooltipWidth > 0) {
        placement = 'left';
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - margin - tooltipWidth;
      }
      // Fallback: center of screen
      else {
        placement = 'bottom';
        top = viewportHeight / 2 - tooltipHeight / 2;
        left = viewportWidth / 2 - tooltipWidth / 2;
      }
    }

    // Ensure tooltip stays within viewport bounds
    left = Math.max(16, Math.min(left, viewportWidth - tooltipWidth - 16));
    top = Math.max(16, Math.min(top, viewportHeight - tooltipHeight - 16));

    setTooltipPosition({ top, left, placement });
  }, [currentStep]);

  /**
   * Recalculate positions on window resize or step change
   */
  useEffect(() => {
    if (currentStep > 0) {
      calculatePositions();

      const handleResize = () => calculatePositions();
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleResize);
      };
    }
  }, [currentStep, calculatePositions]);

  /**
   * Handle keyboard navigation
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleSkip();
      } else if (e.key === 'Enter') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep]);

  /**
   * Start the tour (move from welcome to step 1)
   */
  const handleStartTour = () => {
    setCurrentStep(1);
  };

  /**
   * Move to next step or complete
   */
  const handleNext = () => {
    if (currentStep === 0) {
      handleStartTour();
    } else if (currentStep < INTRO_STEPS.length) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  /**
   * Skip the entire tour
   */
  const handleSkip = () => {
    onComplete();
  };

  /**
   * Render welcome screen (step 0)
   */
  const renderWelcomeScreen = () => (
    <div className={styles.welcomeScreen}>
      <div className={styles.welcomeContent}>
        <h1 className={styles.welcomeTitle}>{t.intro.title}</h1>
        <p className={styles.welcomeMessage}>{t.intro.message}</p>

        {/* Video preview of isochrone animation */}
        <div className={styles.animationPreview}>
          <video
            className={styles.demoVideo}
            autoPlay
            loop
            muted
            playsInline
            poster="/isochrone-demo-poster.png"
          >
            <source src="/isochrone-demo.mp4" type="video/mp4" />
            {/* Fallback text if video doesn't load */}
            Your browser does not support video playback.
          </video>
        </div>

        <div className={styles.welcomeButtons}>
          <button
            type="button"
            onClick={handleStartTour}
            className={styles.startButton}
            aria-label={t.intro.startTour}
          >
            {t.intro.startTour}
          </button>
          <button
            type="button"
            onClick={handleSkip}
            className={styles.skipButton}
            aria-label={t.intro.skip}
          >
            {t.intro.skip}
          </button>
        </div>
      </div>
    </div>
  );

  /**
   * Render spotlight tour (steps 1-5)
   */
  const renderSpotlightTour = () => {
    const stepConfig = INTRO_STEPS[currentStep - 1];
    const isLastStep = currentStep === INTRO_STEPS.length;

    return (
      <>
        {/* Dark overlay */}
        <div className={styles.overlay} onClick={handleSkip} />

        {/* Spotlight highlight */}
        {spotlightPosition && (
          <div
            className={styles.spotlight}
            style={{
              left: spotlightPosition.x,
              top: spotlightPosition.y,
              width: spotlightPosition.width,
              height: spotlightPosition.height,
            }}
          />
        )}

        {/* Tooltip card */}
        {tooltipPosition && (
          <div
            className={`${styles.tooltip} ${styles[tooltipPosition.placement]} ${currentStep === 5 ? styles.shareStep : ''}`}
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
            }}
          >
            <div className={styles.tooltipContent}>
              <div className={styles.tooltipHeader}>
                <span className={styles.stepIndicator}>
                  {currentStep} / {INTRO_STEPS.length}
                </span>
              </div>
              <p className={styles.tooltipDescription}>
                {t.intro[stepConfig.descriptionKey as keyof typeof t.intro]}
              </p>
              <div className={styles.tooltipButtons}>
                <button
                  type="button"
                  onClick={handleNext}
                  className={styles.nextButton}
                  aria-label={isLastStep ? t.intro.gotIt : t.intro.next}
                >
                  {isLastStep ? t.intro.gotIt : t.intro.next}
                </button>
                <button
                  type="button"
                  onClick={handleSkip}
                  className={styles.skipButtonSmall}
                  aria-label={t.intro.skip}
                >
                  {t.intro.skip}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className={styles.introScreen} role="dialog" aria-modal="true" aria-labelledby="intro-title">
      {currentStep === 0 ? renderWelcomeScreen() : renderSpotlightTour()}
    </div>
  );
};

